import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calculator, Check, Copy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type CalcRow = {
  label: string;
  value: string;
  binary?: string;
  note?: string;
};

type SubnetRow = {
  network: string;
  broadcast: string;
  hostMin: string;
  hostMax: string;
  hosts: string;
};

type CalcResult = {
  rows: CalcRow[];
  subnets: SubnetRow[];
  subnetInfo?: string;
};

function parseIp(value: string): number {
  const parts = value.trim().split(".");

  if (parts.length !== 4) {
    throw new Error("ipcalc.invalidIp");
  }

  const octets = parts.map((part) => {
    if (!/^\d+$/.test(part)) {
      throw new Error("ipcalc.invalidIp");
    }

    const number = Number(part);

    if (number < 0 || number > 255) {
      throw new Error("ipcalc.invalidIp");
    }

    return number;
  });

  return (
    ((octets[0] ?? 0) * 256 + (octets[1] ?? 0)) * 256 * 256 +
    (octets[2] ?? 0) * 256 +
    (octets[3] ?? 0)
  );
}

function toIp(value: number): string {
  const safe = Math.max(0, Math.min(0xffffffff, Math.floor(value)));

  return [
    Math.floor(safe / 16777216) % 256,
    Math.floor(safe / 65536) % 256,
    Math.floor(safe / 256) % 256,
    safe % 256,
  ].join(".");
}

function maskFromPrefix(prefix: number): number {
  if (prefix < 0 || prefix > 32) {
    throw new Error("ipcalc.invalidNetmask");
  }

  if (prefix === 0) return 0;

  return 0xffffffff - (2 ** (32 - prefix) - 1);
}

function wildcardFromPrefix(prefix: number): number {
  return 0xffffffff - maskFromPrefix(prefix);
}

function prefixFromMaskValue(mask: number): number | null {
  for (let prefix = 0; prefix <= 32; prefix++) {
    if (maskFromPrefix(prefix) === mask) {
      return prefix;
    }
  }

  return null;
}

function parsePrefix(value: string, ipValue: number): number {
  const trimmed = value.trim().replace(/^\//, "");

  if (trimmed === "") {
    return defaultPrefix(ipValue);
  }

  if (/^\d+$/.test(trimmed)) {
    const prefix = Number(trimmed);

    if (prefix >= 0 && prefix <= 32) {
      return prefix;
    }
  }

  const maskValue = parseIp(trimmed);
  const normalPrefix = prefixFromMaskValue(maskValue);

  if (normalPrefix !== null) {
    return normalPrefix;
  }

  const inversePrefix = prefixFromMaskValue(0xffffffff - maskValue);

  if (inversePrefix !== null) {
    return inversePrefix;
  }

  throw new Error("ipcalc.invalidNetmask");
}

function defaultPrefix(ipValue: number): number {
  const firstOctet = Math.floor(ipValue / 16777216) % 256;

  if (firstOctet <= 127) return 8;
  if (firstOctet <= 191) return 16;
  if (firstOctet <= 223) return 24;

  return 32;
}

function ipClass(ipValue: number): string {
  const firstOctet = Math.floor(ipValue / 16777216) % 256;

  if (firstOctet <= 127) return "A";
  if (firstOctet <= 191) return "B";
  if (firstOctet <= 223) return "C";
  if (firstOctet <= 239) return "D";
  return "E";
}

function isPrivate(ipValue: number): boolean {
  const first = Math.floor(ipValue / 16777216) % 256;
  const second = Math.floor(ipValue / 65536) % 256;

  return (
    first === 10 ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168)
  );
}

function blockSize(prefix: number): number {
  return 2 ** (32 - prefix);
}

function networkOf(ipValue: number, prefix: number): number {
  const size = blockSize(prefix);
  return Math.floor(ipValue / size) * size;
}

function broadcastOf(networkValue: number, prefix: number): number {
  return networkValue + blockSize(prefix) - 1;
}

function hostsPerNet(prefix: number): string {
  if (prefix === 32) return "1";
  if (prefix === 31) return "2";

  return String(2 ** (32 - prefix) - 2);
}

function hostMin(networkValue: number, prefix: number): number {
  if (prefix >= 31) return networkValue;
  return networkValue + 1;
}

function hostMax(broadcastValue: number, prefix: number): number {
  if (prefix >= 31) return broadcastValue;
  return broadcastValue - 1;
}

function toBinary(value: number, prefix?: number): string {
  const bits = toIp(value)
    .split(".")
    .map((part) => Number(part).toString(2).padStart(8, "0"))
    .join("");

  if (typeof prefix !== "number") {
    return bits.match(/.{1,8}/g)?.join(".") ?? bits;
  }

  const marked = `${bits.slice(0, prefix)} ${bits.slice(prefix)}`.trim();

  return marked;
}

function calculate(
  address: string,
  mask: string,
  secondMask: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): CalcResult {
  const ipValue = parseIp(address);
  const prefix = parsePrefix(mask, ipValue);
  const netmaskValue = maskFromPrefix(prefix);
  const wildcardValue = wildcardFromPrefix(prefix);
  const networkValue = networkOf(ipValue, prefix);
  const broadcastValue = broadcastOf(networkValue, prefix);
  const hostMinValue = hostMin(networkValue, prefix);
  const hostMaxValue = hostMax(broadcastValue, prefix);
  const className = ipClass(ipValue);
  const privateText = isPrivate(ipValue) ? t("ipcalc.privateInternet") : "";

  const rows: CalcRow[] = [
    {
      label: t("ipcalc.address"),
      value: toIp(ipValue),
      binary: toBinary(ipValue, prefix),
    },
    {
      label: t("ipcalc.netmask"),
      value: `${toIp(netmaskValue)} = ${prefix}`,
      binary: toBinary(netmaskValue, prefix),
    },
    {
      label: t("ipcalc.wildcard"),
      value: toIp(wildcardValue),
      binary: toBinary(wildcardValue, prefix),
    },
    {
      label: t("ipcalc.network"),
      value: `${toIp(networkValue)}/${prefix}`,
      binary: toBinary(networkValue, prefix),
      note: t("ipcalc.class", { className }),
    },
    {
      label: t("ipcalc.broadcast"),
      value: toIp(broadcastValue),
      binary: toBinary(broadcastValue, prefix),
    },
    {
      label: t("ipcalc.hostMin"),
      value: toIp(hostMinValue),
      binary: toBinary(hostMinValue, prefix),
    },
    {
      label: t("ipcalc.hostMax"),
      value: toIp(hostMaxValue),
      binary: toBinary(hostMaxValue, prefix),
    },
    {
      label: t("ipcalc.hostsPerNet"),
      value: hostsPerNet(prefix),
      note: privateText,
    },
  ];

  const subnets: SubnetRow[] = [];
  let subnetInfo: string | undefined;

  if (secondMask.trim() !== "") {
    const secondPrefix = parsePrefix(secondMask, ipValue);

    if (secondPrefix > prefix) {
      const amount = 2 ** (secondPrefix - prefix);
      const maxRows = Math.min(amount, 64);
      const size = blockSize(secondPrefix);

      for (let index = 0; index < maxRows; index++) {
        const subnetNetwork = networkValue + index * size;
        const subnetBroadcast = broadcastOf(subnetNetwork, secondPrefix);

        subnets.push({
          network: `${toIp(subnetNetwork)}/${secondPrefix}`,
          broadcast: toIp(subnetBroadcast),
          hostMin: toIp(hostMin(subnetNetwork, secondPrefix)),
          hostMax: toIp(hostMax(subnetBroadcast, secondPrefix)),
          hosts: hostsPerNet(secondPrefix),
        });
      }

      subnetInfo = t("ipcalc.subnetInfo", {
        count: amount,
        shown: maxRows,
        prefix: secondPrefix,
      });
    } else if (secondPrefix < prefix) {
      const superNetwork = networkOf(ipValue, secondPrefix);
      const superBroadcast = broadcastOf(superNetwork, secondPrefix);

      subnets.push({
        network: `${toIp(superNetwork)}/${secondPrefix}`,
        broadcast: toIp(superBroadcast),
        hostMin: toIp(hostMin(superNetwork, secondPrefix)),
        hostMax: toIp(hostMax(superBroadcast, secondPrefix)),
        hosts: hostsPerNet(secondPrefix),
      });

      subnetInfo = t("ipcalc.supernetInfo", { prefix: secondPrefix });
    } else {
      subnetInfo = t("ipcalc.sameMask");
    }
  }

  return {
    rows,
    subnets,
    subnetInfo,
  };
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

function ResultRow({ row }: { row: CalcRow }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await copyText(row.value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="grid gap-2 rounded-md border bg-muted/30 p-3 md:grid-cols-[150px_1fr_auto]">
      <div className="font-semibold text-muted-foreground">{row.label}</div>
      <div className="space-y-1">
        <div className="font-mono text-sm">
          {row.value}
          {row.note ? (
            <span className="ml-2 font-sans text-xs text-muted-foreground">
              {row.note}
            </span>
          ) : null}
        </div>
        {row.binary ? (
          <div className="break-all font-mono text-xs text-muted-foreground">
            {row.binary}
          </div>
        ) : null}
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={handleCopy}
        title={copied ? t("common.copied") : t("common.copy")}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

export function IPCalculatorForm() {
  const { t } = useTranslation();
  const [address, setAddress] = useState("192.168.0.1");
  const [netmask, setNetmask] = useState("24");
  const [secondMask, setSecondMask] = useState("");

  const calculation = useMemo(() => {
    try {
      return {
        result: calculate(address, netmask, secondMask, t),
        error: "",
      };
    } catch (error) {
      return {
        result: null,
        error: error instanceof Error ? t(error.message) : t("ipcalc.invalidInput"),
      };
    }
  }, [address, netmask, secondMask, t]);

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="text-lg font-semibold">{t("ipcalc.title")}</h2>
            <p className="text-sm text-muted-foreground">{t("ipcalc.subtitle")}</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="ipcalc-address">{t("ipcalc.addressInput")}</Label>
            <Input
              id="ipcalc-address"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              placeholder="192.168.0.1"
              className="font-mono"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ipcalc-netmask">{t("ipcalc.netmaskInput")}</Label>
            <Input
              id="ipcalc-netmask"
              value={netmask}
              onChange={(event) => setNetmask(event.target.value)}
              placeholder="24 ou 255.255.255.0"
              className="font-mono"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ipcalc-second-mask">{t("ipcalc.secondNetmaskInput")}</Label>
            <Input
              id="ipcalc-second-mask"
              value={secondMask}
              onChange={(event) => setSecondMask(event.target.value)}
              placeholder="25"
              className="font-mono"
              autoComplete="off"
            />
          </div>
        </div>

        {calculation.error ? (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive-foreground">
            {calculation.error}
          </div>
        ) : null}

        {calculation.result ? (
          <div className="space-y-3">
            {calculation.result.rows.map((row) => (
              <ResultRow key={row.label} row={row} />
            ))}

            {calculation.result.subnetInfo ? (
              <div className="space-y-3 pt-2">
                <h3 className="font-semibold">{t("ipcalc.subSupernet")}</h3>
                <p className="text-sm text-muted-foreground">
                  {calculation.result.subnetInfo}
                </p>

                {calculation.result.subnets.length > 0 ? (
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full min-w-[720px] text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-3 py-2 text-left">{t("ipcalc.network")}</th>
                          <th className="px-3 py-2 text-left">{t("ipcalc.hostMin")}</th>
                          <th className="px-3 py-2 text-left">{t("ipcalc.hostMax")}</th>
                          <th className="px-3 py-2 text-left">{t("ipcalc.broadcast")}</th>
                          <th className="px-3 py-2 text-left">{t("ipcalc.hostsPerNet")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calculation.result.subnets.map((subnet) => (
                          <tr key={subnet.network} className="border-t">
                            <td className="px-3 py-2 font-mono">{subnet.network}</td>
                            <td className="px-3 py-2 font-mono">{subnet.hostMin}</td>
                            <td className="px-3 py-2 font-mono">{subnet.hostMax}</td>
                            <td className="px-3 py-2 font-mono">{subnet.broadcast}</td>
                            <td className="px-3 py-2 font-mono">{subnet.hosts}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
